import React, {
  createContext,
  ComponentType,
  useContext,
  useEffect,
  useMemo,
} from 'react'
import { ProductLine } from '@adc/constants'
import type { Theme } from '../../types'

const ApplicationThemeContext = createContext<Theme | null>(null)

const ApplicationThemeConsumer = ApplicationThemeContext.Consumer

interface ApplicationThemeProviderProps {
  children: React.ReactNode
  theme: Theme
}

const ApplicationThemeProvider = ({
  children,
  theme: _theme,
}: ApplicationThemeProviderProps) => {
  const theme = useMemo(
    () => ({
      ...({ productLine: ProductLine.AuctionCom, version: 1 } as const),
      ..._theme,
    }),
    [_theme]
  )

  useEffect(() => {
    const version = theme.version ?? 1
    document.documentElement.setAttribute(
      'data-theme',
      `${theme.productLine}--${version}`
    )
    return () => {
      document.documentElement.removeAttribute('data-theme')
    }
  }, [theme.productLine, theme.version])

  return (
    <ApplicationThemeContext.Provider value={theme}>
      {children}
    </ApplicationThemeContext.Provider>
  )
}

const DEFAULT_THEME: Theme = {
  productLine: ProductLine.AuctionCom,
  version: 1,
}

const useApplicationTheme = () => {
  const context = useContext(ApplicationThemeContext)
  return context ?? DEFAULT_THEME
}

/** Returns the raw context value — null when no ApplicationThemeProvider is present. Use useApplicationTheme for general consumption. */
const useApplicationThemeRaw = () => useContext(ApplicationThemeContext)

interface WithApplicationThemeProps {
  theme: Theme | null
}

const withApplicationTheme =
  <P extends object>(Component: ComponentType<P & WithApplicationThemeProps>) =>
  (props: Omit<P, keyof WithApplicationThemeProps>) =>
    (
      <ApplicationThemeConsumer>
        {theme => (
          <Component theme={theme ?? DEFAULT_THEME} {...(props as P)} />
        )}
      </ApplicationThemeConsumer>
    )

export {
  ApplicationThemeProvider,
  ApplicationThemeConsumer,
  useApplicationTheme,
  useApplicationThemeRaw,
  withApplicationTheme,
  ProductLine,
}
